import { LocalizedLink } from "@/i18n/language";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHeader from "@/components/feature/PageHeader";
import BookingCompanyNotice from "@/components/feature/BookingCompanyNotice";
import GalleryGrid from "@/components/feature/GalleryGrid";
import {
  FloorPlanTabs,
  FloorPlanImage,
} from "@/components/feature/VillaFloorPlan";
import { img } from "@/lib/images";
import { usePriceMap } from "@/hooks/usePrices";
import { useGalleryCaptions } from "@/hooks/useGalleryCaptions";
import { hibiskusGalleryImages } from "./gallery-data";

export default function VillaHibiskusPage() {
  const { i18n } = useTranslation();
  const isDe = i18n.language.startsWith("de");
  const { priceMap, money } = usePriceMap();
  const galleryImages = useGalleryCaptions("villa-hibiscus", hibiskusGalleryImages);

  const [planOpen, setPlanOpen] = useState(false);

  const roomFeatures = isDe
    ? [
        "3 Zimmer mit jeweils eigenem Badezimmer/WC & Dusche",
        "Handtücher, Toilettenartikel (Duschgel, Shampoo, Conditioner)",
        "Haarföhn",
        "Kleiderablage",
        "Leselampen beim Bett",
        "Klimaanlage in allen Schlafzimmern",
        "kein TV im Zimmer",
        "täglich 1 Flasche Mineralwasser pro Person kostenlos",
      ]
    : [
        "3 rooms each with private bathroom/WC & shower",
        "Towels, toiletries (shower gel, shampoo, conditioner)",
        "Hair dryer",
        "Clothes rack",
        "Reading lamps at the bed",
        "Air conditioning in all bedrooms",
        "No TV in the room",
        "Daily 1 bottle of mineral water per person free",
      ];

  const livingFeatures = isDe
    ? [
        "großes Wohnzimmer mit großer Sitzecke",
        '55" Fernseher mit Soundanlage',
      ]
    : [
        "Large living room with big seating area",
        '55" TV with sound system',
      ];

  const kitchenFeatures = isDe
    ? [
        "voll eingerichtete Küche mit Kühlschrank, Geschirr und Gasofen",
      ]
    : [
        "Fully equipped kitchen with refrigerator, dishes and gas stove",
      ];

  const terraceFeatures = isDe
    ? [
        "große Speiseterrasse mit großem Esstisch und 8 Stühlen",
      ]
    : [
        "Large dining terrace with large dining table and 8 chairs",
      ];

  const poolFeatures = isDe
    ? [
        "großes Pool 11x6 Meter mit 6 Liegen, Schirmen und Außendusche",
      ]
    : [
        "Large pool 11x6 meters with 6 loungers, umbrellas and outdoor shower",
      ];

  return (
    <div className="bg-background-50 text-foreground-950 min-h-screen">
      <Navbar />
      <main>
        <PageHeader
          eyebrow={isDe ? "Villa Hibiskus" : "Villa Hibiscus"}
          title={isDe ? "Drei Schlafzimmer" : "Three Bedrooms"}
          eyebrowSize="text-lg md:text-2xl"
          titleSize="text-lg md:text-xl"
          subtitle={
            isDe
              ? "Alle Details unserer größten Villa"
              : "All details of our largest villa"
          }
          image={img("villas/villa-hibiscus/header-villahibiscus-01.jpg")}
          imageAlt={isDe ? "Villa Hibiskus" : "Villa Hibiscus"}
        />

        <section className="py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column */}
            <div className="lg:col-span-2">
              {/* Gallery */}
              <div className="mb-8">
                <GalleryGrid images={galleryImages} />
              </div>

              {/* Parameters */}
              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-full text-sm text-foreground-700">
                  <i className="ri-user-line text-accent-700"></i>
                  {isDe ? "Max 6 Personen" : "Max 6 persons"}
                </div>
                <div className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-full text-sm text-foreground-700">
                  <i className="ri-hotel-bed-line text-accent-700"></i>
                  {isDe ? "3 King Size Betten" : "3 King Size beds"}
                </div>
                <div className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-full text-sm text-foreground-700">
                  <i className="ri-ruler-line text-accent-700"></i>
                  {isDe ? "230 qm" : "230 sqm"}
                </div>
                <FloorPlanTabs
                  open={planOpen}
                  onToggle={() => setPlanOpen((v) => !v)}
                />
              </div>

              {/* Floor plan */}
              {planOpen && (
              <FloorPlanImage
                deImg={img("villas/villa-hibiscus/floor-plan-villa-hibiskus_ger.png")}
                enImg={img("villas/villa-hibiscus/floor-plan-villa-hibiskus_eng.png")}
                alt={isDe ? "Grundriss Villa Hibiskus" : "Floor plan Villa Hibiscus"}
              />
              )}

              {/* Description */}
              <p className="text-foreground-700 leading-relaxed mb-8 text-base">
                {isDe
                  ? "Die größte unserer Villen bietet ausreichend Platz für sechs Personen und es stehen alle Einrichtungen wie Garten, Pool und Speiseterrasse zur alleinigen Verfügung."
                  : "The largest of our villas offers ample space for six people and all facilities such as garden, pool and dining terrace are available for exclusive use."}
              </p>

              {/* Features */}
              <div className="space-y-8">
                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-star-line text-accent-700"></i>
                    {isDe ? "ZIMMER" : "ROOMS"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {roomFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-sofa-line text-accent-700"></i>
                    {isDe ? "WOHNZIMMER" : "LIVING ROOM"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {livingFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-restaurant-line text-accent-700"></i>
                    {isDe ? "KÜCHE" : "KITCHEN"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {kitchenFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-sun-line text-accent-700"></i>
                    {isDe ? "TERRASSE" : "TERRACE"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {terraceFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-water-flash-line text-accent-700"></i>
                    {isDe ? "POOL & GARTEN" : "POOL & GARDEN"}
                  </h3>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {poolFeatures.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm text-foreground-700"
                      >
                        <i className="ri-check-line text-accent-700 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Right column */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Price box */}
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <div className="text-xs text-foreground-500 uppercase tracking-wider mb-1">
                    {isDe ? "Preis" : "Price"}
                  </div>
                  <div className="text-3xl font-heading text-foreground-950 mb-1">
                    {money(priceMap["villa_hibiskus_2"])}
                  </div>
                  <div className="text-sm text-foreground-600 mb-4">
                    {isDe ? "2 Zimmer / Nacht · inklusive Frühstück" : "2 rooms / night · incl. breakfast"}
                  </div>
                  <div className="text-3xl font-heading text-foreground-950 mb-1">
                    {money(priceMap["villa_hibiskus_3"])}
                  </div>
                  <div className="text-sm text-foreground-600 mb-6">
                    {isDe ? "3 Zimmer / Nacht · inklusive Frühstück" : "3 rooms / night · incl. breakfast"}
                  </div>
                  <LocalizedLink
                    to="/villa-reservation?villa=villa-hibiskus"
                    className="block w-full text-center bg-accent-500 hover:bg-accent-100 text-background-50 hover:text-foreground-950 py-3 rounded-md font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    <i className="ri-calendar-check-line mr-2"></i>
                    {isDe ? "Jetzt buchen" : "Book now"}
                  </LocalizedLink>
                  <BookingCompanyNotice isDe={isDe} />
                </div>

                {/* Other villas */}
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <h3 className="font-heading text-lg text-foreground-950 mb-4">
                    {isDe ? "Weitere Villen" : "More Villas"}
                  </h3>
                  <div className="space-y-4">
                    <LocalizedLink
                      to="/villa-frangipani"
                      className="flex items-center gap-3 group"
                    >
                      <img
                        src={img("villas/villa-frangipani/villa-frangipani-view-01.jpg")}
                        alt="Villa Frangipani"
                        className="w-14 h-14 rounded object-cover flex-shrink-0"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                          Villa Frangipani
                        </div>
                        <div className="text-xs text-foreground-500">
                          {isDe ? "2 Zimmer · 110 qm" : "2 rooms · 110 sqm"}
                        </div>
                      </div>
                    </LocalizedLink>
                    <LocalizedLink
                      to="/villa-bougainvillea"
                      className="flex items-center gap-3 group"
                    >
                      <img
                        src={img("villas/villa-bougainvillea/villa-bougainvillea-view-01.jpg")}
                        alt="Villa Bougainvillea"
                        className="w-14 h-14 rounded object-cover flex-shrink-0"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors">
                          Villa Bougainvillea
                        </div>
                        <div className="text-xs text-foreground-500">
                          {isDe ? "1 Zimmer · 78 qm" : "1 room · 78 sqm"}
                        </div>
                      </div>
                    </LocalizedLink>
                  </div>
                </div>

                {/* Contact */}
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <h3 className="font-heading text-lg text-foreground-950 mb-4">
                    {isDe ? "Kontakt" : "Contact"}
                  </h3>
                  <div className="space-y-3 text-sm text-foreground-700">
                    <div className="flex items-center gap-2">
                      <i className="ri-phone-line text-accent-700"></i>
                      <span>+62 81 246 21 8000</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-mail-line text-accent-700"></i>
                      <span>office@balivwtour.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}